import PropTypes from 'prop-types';
import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import TableActionButton from '../TableActionButton';
import {
  FilterOverlay,
  FilterDropdown,
  FilterSection,
  FilterTitle,
  FilterOptions,
  FilterOption,
  FilterCheckbox,
  FilterRadio,
  FilterActions,
  FilterButton,
  FilterSelect,
  FilterWrapper,
} from './StyledComponents';

/**
 * TableFilter - A reusable filter component for tables
 *
 * @param {Array} filters - Array of filter configurations
 * @param {Object} values - Current filter values
 * @param {Function} onChange - Callback when filter values change
 * @param {Function} onApply - Callback when filters are applied
 * @param {Function} onClear - Callback when filters are cleared
 * @param {boolean} showButton - Whether to show the filter toggle button (default: true)
 * @param {boolean} isOpen - Controlled open state (optional)
 * @param {Function} onToggle - Callback when filter is toggled (optional)
 *
 * Filter configuration object:
 * {
 *   key: string,           // Unique identifier for the filter
 *   label: string,         // Display label
 *   type: 'select' | 'radio' | 'checkbox',
 *   options: Array<{ label: string, value: string | number }>
 * }
 */
const TableFilter = ({
  filters = [],
  values = {},
  onChange,
  onApply,
  onClear,
  showButton = true,
  isOpen: controlledIsOpen,
  onToggle,
}) => {
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const [pendingValues, setPendingValues] = useState(values);

  // Use controlled or uncontrolled state
  const isOpen =
    controlledIsOpen !== undefined ? controlledIsOpen : internalIsOpen;

  // Sync pending values with external values when modal opens
  useEffect(() => {
    if (isOpen) {
      setPendingValues(values);
    }
  }, [isOpen, values]);

  const toggleFilter = useCallback(() => {
    const newState = !isOpen;

    if (onToggle) {
      onToggle(newState);
    } else {
      setInternalIsOpen(newState);
    }

    // Prevent body scroll on mobile
    if (window.innerWidth <= 768) {
      if (newState) {
        document.body.style.overflow = 'hidden';
        document.body.style.position = 'fixed';
        document.body.style.width = '100%';
      } else {
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.width = '';
      }
    }
  }, [isOpen, onToggle]);

  // Cleanup body styles on unmount
  useEffect(() => {
    return () => {
      if (isOpen && window.innerWidth <= 768) {
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.width = '';
      }
    };
  }, [isOpen]);

  const handleValueChange = (key, value) => {
    const newValues = { ...pendingValues, [key]: value };
    setPendingValues(newValues);
    if (onChange) {
      onChange(newValues);
    }
  };

  const handleRadioChange = (key, value) => {
    // For radio, toggle off if clicking the same value
    const newValue = pendingValues[key] === value ? '' : value;
    handleValueChange(key, newValue);
  };

  const handleCheckboxChange = (key, optionValue) => {
    const currentValues = pendingValues[key] || [];
    const newValues = currentValues.includes(optionValue)
      ? currentValues.filter((v) => v !== optionValue)
      : [...currentValues, optionValue];
    handleValueChange(key, newValues);
  };

  const handleApply = () => {
    if (onApply) {
      onApply(pendingValues);
    }
    toggleFilter();
  };

  // Clear inside popup - only clears selections without closing
  const handleClearInPopup = () => {
    const clearedValues = {};
    filters.forEach((filter) => {
      if (filter.type === 'checkbox') {
        clearedValues[filter.key] = [];
      } else {
        clearedValues[filter.key] = '';
      }
    });
    setPendingValues(clearedValues);
  };

  // Clear outside popup - clears and triggers refetch without opening popup
  const handleClearOutside = () => {
    const clearedValues = {};
    filters.forEach((filter) => {
      if (filter.type === 'checkbox') {
        clearedValues[filter.key] = [];
      } else {
        clearedValues[filter.key] = '';
      }
    });
    setPendingValues(clearedValues);
    if (onClear) {
      onClear(clearedValues);
    }
  };

  // Check if any filters are active
  const hasActiveFilters = Object.values(values).some((value) => {
    if (Array.isArray(value)) return value.length > 0;
    return value !== '' && value !== null && value !== undefined;
  });

  const renderFilterContent = (filter) => {
    switch (filter.type) {
      case 'select':
        return (
          <FilterSelect
            value={pendingValues[filter.key] || ''}
            onChange={(e) => handleValueChange(filter.key, e.target.value)}
          >
            <option value="">{filter.placeholder || 'All'}</option>
            {filter.options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </FilterSelect>
        );

      case 'radio':
        return (
          <FilterOptions>
            {filter.options.map((option) => (
              <FilterOption
                key={option.value}
                onClick={() => handleRadioChange(filter.key, option.value)}
                selected={pendingValues[filter.key] === option.value}
              >
                <FilterRadio
                  checked={pendingValues[filter.key] === option.value}
                />
                <span>{option.label}</span>
              </FilterOption>
            ))}
          </FilterOptions>
        );

      case 'checkbox':
        return (
          <FilterOptions>
            {filter.options.map((option) => {
              const currentValues = pendingValues[filter.key] || [];
              const isChecked = currentValues.includes(option.value);
              return (
                <FilterOption
                  key={option.value}
                  onClick={() => handleCheckboxChange(filter.key, option.value)}
                  selected={isChecked}
                >
                  <FilterCheckbox checked={isChecked} />
                  <span>{option.label}</span>
                </FilterOption>
              );
            })}
          </FilterOptions>
        );

      default:
        return null;
    }
  };

  const filterDropdown = isOpen && (
    <>
      <FilterOverlay show={isOpen} onClick={toggleFilter} />
      <FilterDropdown>
        <div>
          {filters.map((filter) => (
            <FilterSection key={filter.key}>
              <FilterTitle>{filter.label}</FilterTitle>
              {renderFilterContent(filter)}
            </FilterSection>
          ))}
          <FilterActions>
            <FilterButton variant="secondary" onClick={handleClearInPopup}>
              Clear
            </FilterButton>
            <FilterButton variant="primary" onClick={handleApply}>
              Apply Filters
            </FilterButton>
          </FilterActions>
        </div>
      </FilterDropdown>
    </>
  );

  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;

  return (
    <FilterWrapper>
      {showButton && (
        <TableActionButton
          onFilterClick={toggleFilter}
          onClearClick={handleClearOutside}
          hasActiveFilters={hasActiveFilters}
        />
      )}
      {isMobile && isOpen
        ? ReactDOM.createPortal(filterDropdown, document.body)
        : filterDropdown}
    </FilterWrapper>
  );
};

TableFilter.propTypes = {
  filters: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      type: PropTypes.oneOf(['select', 'radio', 'checkbox']).isRequired,
      options: PropTypes.arrayOf(
        PropTypes.shape({
          label: PropTypes.string.isRequired,
          value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
            .isRequired,
        })
      ).isRequired,
      placeholder: PropTypes.string,
    })
  ).isRequired,
  values: PropTypes.object,
  onChange: PropTypes.func,
  onApply: PropTypes.func,
  onClear: PropTypes.func,
  showButton: PropTypes.bool,
  isOpen: PropTypes.bool,
  onToggle: PropTypes.func,
};

export default TableFilter;

// Export styled components for custom implementations
export {
  FilterOverlay,
  FilterDropdown,
  FilterSection,
  FilterTitle,
  FilterOptions,
  FilterOption,
  FilterCheckbox,
  FilterRadio,
  FilterActions,
  FilterButton,
  FilterSelect,
  FilterWrapper,
  FilterIconButton,
} from './StyledComponents';
